import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';

import { isRulesSelectableType } from '../lib/rules-field-eligibility';

import { useFormBuilderStore } from './form-builder.store';
import { useFormPermissionsStore } from './form-permissions.store';

import type { PermissionFieldTarget, PermissionsGrantsSnapshot } from './form-permissions.store';
import type { FormElement, TableCell, TableElement } from './form.types';
import { clonePlain, useLimitedHistory } from './useLimitedHistory';

export interface PermissionsSelectedField {
  kind: 'field';
  id: string;
  displayId: string;
  label: string;
  tableId?: string;
  row?: number;
  col?: number;
}

export interface PermissionsSelectedTable {
  kind: 'table';
  id: string;
  displayId: string;
}

export type PermissionsSelectedItem = PermissionsSelectedField | PermissionsSelectedTable;

export type PermissionsOutline = 'selected' | 'applied' | 'picked';

export interface PermissionsSelectionModifiers {
  additive?: boolean;
  range?: boolean;
}

export interface PermissionsClipboard {
  sourceId: string;
  positionIds: number[];
  functionalActivityIds: number[];
}

interface SelectionHistorySnapshot {
  addMode: boolean;
  fillVisible: boolean;
  confirmedItems: PermissionsSelectedItem[];
  draftItems: PermissionsSelectedItem[];
  anchorId: string | null;
  selectAllActive: boolean;
  selectedPositionIds: string[];
  selectedFunctionalActivityIds: string[];
  grants: PermissionsGrantsSnapshot;
}

const FALLBACK_LABEL = 'Campo sem nome';

interface StoredSelection {
  confirmedItems: PermissionsSelectedItem[];
  positionIds: string[];
  functionalActivityIds: string[];
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'string');
}

function isSelectedItem(value: unknown): value is PermissionsSelectedItem {
  if (typeof value !== 'object' || value === null) return false;
  const item = value as Record<string, unknown>;
  const hasKeys = typeof item.id === 'string' && typeof item.displayId === 'string';
  if (item.kind === 'table') return hasKeys;
  return item.kind === 'field' && hasKeys && typeof item.label === 'string';
}

function parseStoredSelection(raw: string): StoredSelection | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    const data = parsed as Record<string, unknown>;
    if (!Array.isArray(data.confirmedItems) || !data.confirmedItems.every(isSelectedItem)) {
      return null;
    }
    if (!isStringArray(data.positionIds) || !isStringArray(data.functionalActivityIds)) return null;
    return {
      confirmedItems: data.confirmedItems,
      positionIds: data.positionIds,
      functionalActivityIds: data.functionalActivityIds,
    };
  } catch {
    return null;
  }
}

function toSelectedField(
  element: FormElement,
  position?: { tableId: string; row: number; col: number }
): PermissionsSelectedField {
  return {
    kind: 'field',
    id: element.id,
    displayId: element.properties.name || element.id,
    label: element.properties.label || element.properties.name || FALLBACK_LABEL,
    ...position,
  };
}

function isTable(element: FormElement): element is TableElement {
  return element.type === 'TABLE';
}

function collectTableFields(table: TableElement): PermissionsSelectedField[] {
  const fields: PermissionsSelectedField[] = [];

  table.properties.cells.forEach((row: TableCell[]) => {
    row.forEach((cell: TableCell) => {
      if (!cell.content || !isRulesSelectableType(cell.content.type)) return;
      fields.push(
        toSelectedField(cell.content, {
          tableId: table.id,
          row: cell.rowIndex,
          col: cell.colIndex,
        })
      );
    });
  });

  return fields;
}

function collectRowFields(table: TableElement, rowIndex: number): PermissionsSelectedField[] {
  return collectTableFields(table).filter((field) => field.row === rowIndex);
}

// The reading order the form shows: top to bottom, and left to right inside a table row.
function collectAllFields(elements: readonly FormElement[]): PermissionsSelectedField[] {
  return elements.flatMap((element) => {
    if (isTable(element)) return collectTableFields(element);
    return isRulesSelectableType(element.type) ? [toSelectedField(element)] : [];
  });
}

function refreshItems(
  stored: PermissionsSelectedItem[],
  elements: readonly FormElement[]
): PermissionsSelectedItem[] {
  const fields = new Map(collectAllFields(elements).map((field) => [field.id, field]));
  const tables = new Map(elements.filter(isTable).map((table) => [table.id, table]));

  return stored.flatMap((item): PermissionsSelectedItem[] => {
    if (item.kind === 'field') {
      const field = fields.get(item.id);
      return field ? [field] : [];
    }
    const table = tables.get(item.id);
    return table
      ? [{ kind: 'table', id: table.id, displayId: table.properties.name || table.id }]
      : [];
  });
}

function uniqueById<T extends { id: string }>(items: T[]): T[] {
  const seen = new Set<string>();

  return items.filter((item) => {
    if (seen.has(item.id)) return false;
    seen.add(item.id);
    return true;
  });
}

export const useFormPermissionsSelectionStore = defineStore('form-permissions-selection', () => {
  const addMode = ref(false);
  const confirmedItems = ref<PermissionsSelectedItem[]>([]);
  const draftItems = ref<PermissionsSelectedItem[]>([]);
  const anchorId = ref<string | null>(null);
  const fillVisible = ref(false);
  const selectAllActive = ref(false);
  const roundItems = ref<PermissionsSelectedItem[]>([]);
  const clickedFieldId = ref<string | null>(null);
  const pasteItems = ref<PermissionsSelectedItem[]>([]);
  const clipboard = ref<PermissionsClipboard | null>(null);
  const selectedPositionIds = ref<string[]>([]);
  const selectedFunctionalActivityIds = ref<string[]>([]);
  const storageKey = ref<string | null>(null);
  const permissionsStore = useFormPermissionsStore();

  const items = computed<PermissionsSelectedItem[]>(() => [
    ...confirmedItems.value,
    ...draftItems.value,
  ]);
  const totalSelected = computed(() => items.value.length);
  const hasItems = computed(() => items.value.length > 0);

  const selectedTableIds = computed(
    () => new Set(items.value.filter((item) => item.kind === 'table').map((item) => item.id))
  );
  const selectedFieldIds = computed(
    () => new Set(items.value.filter((item) => item.kind === 'field').map((item) => item.id))
  );

  function isFieldSelected(fieldId: string, tableId?: string): boolean {
    if (tableId && selectedTableIds.value.has(tableId)) return true;
    return selectedFieldIds.value.has(fieldId);
  }

  // Blue wins only while the add mode is on; once confirmed, an applied field goes back to wine.
  function getOutline(
    fieldId: string,
    hasAppliedPermission: boolean,
    tableId?: string
  ): PermissionsOutline | null {
    if (addMode.value && isFieldInDraft(fieldId, tableId)) return 'selected';
    if (hasAppliedPermission && clickedFieldId.value === fieldId && !addMode.value) return 'picked';
    if (hasAppliedPermission) return 'applied';
    return isFieldSelected(fieldId, tableId) ? 'selected' : null;
  }

  function isTableSelected(tableId: string): boolean {
    return selectedTableIds.value.has(tableId);
  }

  function isCovered(field: PermissionsSelectedField): boolean {
    return isFieldSelected(field.id, field.tableId);
  }

  function isFieldInDraft(fieldId: string, tableId?: string): boolean {
    return draftItems.value.some(
      (item) =>
        (item.kind === 'field' && item.id === fieldId) ||
        (item.kind === 'table' && item.id === tableId)
    );
  }

  function isDraftField(field: PermissionsSelectedField): boolean {
    return draftItems.value.some((item) => item.kind === 'field' && item.id === field.id);
  }

  function toTarget(field: PermissionsSelectedField): PermissionFieldTarget {
    return {
      fieldId: field.id,
      ...(field.tableId !== undefined ? { tableId: field.tableId } : {}),
      ...(field.row !== undefined ? { row: field.row } : {}),
      ...(field.col !== undefined ? { col: field.col } : {}),
    };
  }

  // A table item stands for every field the table holds, so it expands to one target per field.
  function resolveItemTargets(
    list: readonly PermissionsSelectedItem[],
    elements: readonly FormElement[]
  ): PermissionFieldTarget[] {
    const fieldsById = new Map(collectAllFields(elements).map((field) => [field.id, field]));

    return list.flatMap((item) => {
      if (item.kind === 'field') return [toTarget(fieldsById.get(item.id) ?? item)];

      const table = elements.find((element) => element.id === item.id);
      return table && isTable(table) ? collectTableFields(table).map(toTarget) : [];
    });
  }

  function captureSnapshot(): SelectionHistorySnapshot {
    return clonePlain({
      addMode: addMode.value,
      fillVisible: fillVisible.value,
      confirmedItems: confirmedItems.value,
      draftItems: draftItems.value,
      anchorId: anchorId.value,
      selectAllActive: selectAllActive.value,
      selectedPositionIds: selectedPositionIds.value,
      selectedFunctionalActivityIds: selectedFunctionalActivityIds.value,
      grants: permissionsStore.captureGrants(),
    });
  }

  function restoreSnapshot(snapshot: SelectionHistorySnapshot) {
    const next = clonePlain(snapshot);
    addMode.value = next.addMode;
    fillVisible.value = next.fillVisible;
    confirmedItems.value = next.confirmedItems;
    draftItems.value = next.draftItems;
    anchorId.value = next.anchorId;
    selectAllActive.value = next.selectAllActive;
    selectedPositionIds.value = next.selectedPositionIds;
    selectedFunctionalActivityIds.value = next.selectedFunctionalActivityIds;
    permissionsStore.restoreGrants(next.grants);
  }

  const history = useLimitedHistory<SelectionHistorySnapshot>({
    capture: captureSnapshot,
    restore: restoreSnapshot,
  });

  // A step is recorded only when the action really changed something, so a click that leaves the
  // screen as it was never costs the user one undo.
  function recordChange(mutate: () => void) {
    const before = captureSnapshot();
    mutate();
    if (JSON.stringify(before) !== JSON.stringify(captureSnapshot())) {
      history.track(before);
    }
  }

  function withHistory<TArgs extends unknown[]>(action: (...args: TArgs) => void) {
    return (...args: TArgs) => recordChange(() => action(...args));
  }

  function resolveConfirmedTargets(elements: readonly FormElement[]): PermissionFieldTarget[] {
    return resolveItemTargets(confirmedItems.value, elements);
  }

  function startAdd() {
    addMode.value = true;
    fillVisible.value = true;
    anchorId.value = null;
    roundItems.value = [];
    clickedFieldId.value = null;
    pasteItems.value = [];
  }

  // The paste destination is only what this round selected, listed fields included, never the origin.
  function confirm() {
    addMode.value = false;
    pasteItems.value = uniqueById(roundItems.value);
    roundItems.value = [];
    clickedFieldId.value = null;
    confirmedItems.value = [...confirmedItems.value, ...draftItems.value];
    draftItems.value = [];
    selectAllActive.value = false;
    anchorId.value = null;
  }

  function dismissFill() {
    fillVisible.value = false;
  }

  function replaceDraft(next: PermissionsSelectedItem[]) {
    const confirmedFieldIds = new Set(
      confirmedItems.value.flatMap((item) => (item.kind === 'field' ? [item.id] : []))
    );
    const confirmedTableIds = new Set(
      confirmedItems.value.flatMap((item) => (item.kind === 'table' ? [item.id] : []))
    );
    const coveringTableIds = new Set([
      ...confirmedTableIds,
      ...next.flatMap((item) => (item.kind === 'table' ? [item.id] : [])),
    ]);
    draftItems.value = uniqueById(
      next.filter((item) => {
        if (item.kind === 'table') return !confirmedTableIds.has(item.id);
        if (confirmedFieldIds.has(item.id)) return false;
        return !(item.tableId && coveringTableIds.has(item.tableId));
      })
    );
    fillVisible.value = true;
    selectAllActive.value = false;
  }

  function withoutFieldsOfTable(list: PermissionsSelectedItem[], tableId: string) {
    return list.filter((item) => item.kind === 'table' || item.tableId !== tableId);
  }

  function trackRoundField(target: PermissionsSelectedField, additive: boolean) {
    if (!additive) {
      roundItems.value = [target];
      return;
    }

    const alreadyTracked = roundItems.value.some((item) => item.id === target.id);
    roundItems.value = alreadyTracked
      ? roundItems.value.filter((item) => item.id !== target.id)
      : [...roundItems.value, target];
  }

  function selectRange(
    ordered: PermissionsSelectedField[],
    fieldId: string,
    additive: boolean
  ): boolean {
    if (!anchorId.value) return false;

    const from = ordered.findIndex((field) => field.id === anchorId.value);
    const to = ordered.findIndex((field) => field.id === fieldId);
    if (from === -1 || to === -1) return false;

    const fullRange = ordered.slice(Math.min(from, to), Math.max(from, to) + 1);
    const slice = fullRange.filter((field) => !isCovered(field) || isDraftField(field));
    replaceDraft(additive ? [...draftItems.value, ...slice] : slice);
    roundItems.value = uniqueById(additive ? [...roundItems.value, ...fullRange] : fullRange);
    return true;
  }

  function selectField(
    elements: readonly FormElement[],
    fieldId: string,
    { additive = false, range = false }: PermissionsSelectionModifiers = {}
  ) {
    if (!addMode.value) return;

    const ordered = collectAllFields(elements);
    const target = ordered.find((field) => field.id === fieldId);
    if (!target) return;

    if (range && selectRange(ordered, fieldId, additive)) return;

    anchorId.value = fieldId;
    trackRoundField(target, additive);

    if (target.tableId && isTableSelected(target.tableId)) return;
    if (confirmedItems.value.some((item) => item.id === fieldId)) return;

    if (!additive) {
      replaceDraft([target]);
      return;
    }

    const alreadyDrafted = draftItems.value.some((item) => item.id === fieldId);
    replaceDraft(
      alreadyDrafted
        ? draftItems.value.filter((item) => item.id !== fieldId)
        : [...draftItems.value, target]
    );
  }

  function adoptTable(table: TableElement, additive: boolean) {
    confirmedItems.value = withoutFieldsOfTable(confirmedItems.value, table.id);
    const kept = additive ? withoutFieldsOfTable(draftItems.value, table.id) : [];
    replaceDraft([
      ...kept,
      { kind: 'table', id: table.id, displayId: table.properties.name || table.id },
    ]);
  }

  function selectTable(table: TableElement, { additive = false }: PermissionsSelectionModifiers = {}) {
    if (!addMode.value) return;

    if (confirmedItems.value.some((item) => item.kind === 'table' && item.id === table.id)) return;

    if (additive && isTableSelected(table.id)) {
      removeItem(table.id);
      roundItems.value = roundItems.value.filter((item) => item.id !== table.id);
      return;
    }

    anchorId.value = null;
    roundItems.value = additive
      ? [...roundItems.value, { kind: 'table', id: table.id, displayId: table.properties.name || table.id }]
      : [{ kind: 'table', id: table.id, displayId: table.properties.name || table.id }];
    adoptTable(table, additive);
  }

  function selectRow(
    table: TableElement,
    rowIndex: number,
    { additive = false }: PermissionsSelectionModifiers = {}
  ) {
    if (!addMode.value) return;
    if (isTableSelected(table.id)) return;

    const fields = collectRowFields(table, rowIndex);
    if (fields.length === 0) return;

    if (additive && fields.every(isDraftField)) {
      const rowIds = new Set(fields.map((field) => field.id));
      replaceDraft(draftItems.value.filter((item) => !rowIds.has(item.id)));
      return;
    }

    anchorId.value = fields[0].id;
    roundItems.value = uniqueById(additive ? [...roundItems.value, ...fields] : fields);
    replaceDraft(additive ? [...draftItems.value, ...fields] : fields);
  }

  function selectAll(elements: readonly FormElement[]) {
    if (!addMode.value) return;

    anchorId.value = null;
    roundItems.value = collectAllFields(elements);
    replaceDraft(collectAllFields(elements));
    selectAllActive.value = true;
  }

  function removeItem(itemId: string) {
    confirmedItems.value = confirmedItems.value.filter((item) => item.id !== itemId);
    draftItems.value = draftItems.value.filter((item) => item.id !== itemId);
    roundItems.value = roundItems.value.filter((item) => item.id !== itemId);
    selectAllActive.value = false;
  }

  function clearDraft() {
    draftItems.value = [];
    roundItems.value = [];
    anchorId.value = null;
    selectAllActive.value = false;
  }

  function clearItems() {
    confirmedItems.value = [];
    draftItems.value = [];
    roundItems.value = [];
    pasteItems.value = [];
    anchorId.value = null;
    selectAllActive.value = false;
  }

  function persist() {
    if (!storageKey.value) return;

    const data: StoredSelection = {
      confirmedItems: confirmedItems.value,
      positionIds: selectedPositionIds.value,
      functionalActivityIds: selectedFunctionalActivityIds.value,
    };
    localStorage.setItem(storageKey.value, JSON.stringify(data));
  }

  function restore(key: string | null, elements: readonly FormElement[]) {
    history.reset();
    storageKey.value = null;
    clearItems();
    selectedPositionIds.value = [];
    selectedFunctionalActivityIds.value = [];

    const raw = key ? localStorage.getItem(key) : null;
    const stored = raw ? parseStoredSelection(raw) : null;
    if (key && raw && !stored) localStorage.removeItem(key);

    if (stored) {
      confirmedItems.value = refreshItems(stored.confirmedItems, elements);
      selectedPositionIds.value = stored.positionIds;
      selectedFunctionalActivityIds.value = stored.functionalActivityIds;
    }
    storageKey.value = key;
  }

  watch([confirmedItems, selectedPositionIds, selectedFunctionalActivityIds], persist, {
    deep: true,
  });

  function leaveStage() {
    history.reset();
    addMode.value = false;
    fillVisible.value = false;
    draftItems.value = [];
    anchorId.value = null;
    selectAllActive.value = false;
    storageKey.value = null;
  }

  function reset() {
    history.reset();
    storageKey.value = null;
    selectedPositionIds.value = [];
    selectedFunctionalActivityIds.value = [];
    clearItems();
    addMode.value = false;
    fillVisible.value = false;
    clickedFieldId.value = null;
    clipboard.value = null;
  }

  // A click outside the add mode picks the one field that can be copied or pasted onto.
  function pickField(fieldId: string) {
    if (addMode.value) return;

    pasteItems.value = [];

    const permitted = useFormPermissionsStore().isFieldPermissioned(fieldId);
    clickedFieldId.value = permitted && clickedFieldId.value !== fieldId ? fieldId : null;
  }

  function clearPick() {
    clickedFieldId.value = null;
  }

  const canCopy = computed(
    () =>
      !addMode.value &&
      clickedFieldId.value !== null &&
      useFormPermissionsStore().isFieldPermissioned(clickedFieldId.value)
  );

  function resolvePasteTargets(elements: readonly FormElement[]): PermissionFieldTarget[] {
    if (!clipboard.value || addMode.value) return [];

    const { sourceId } = clipboard.value;
    const picked: PermissionFieldTarget[] =
      clickedFieldId.value !== null
        ? resolveItemTargets([{ kind: 'field', id: clickedFieldId.value, displayId: '', label: '' }], elements)
        : resolveItemTargets(pasteItems.value, elements);

    return picked.filter((target) => target.fieldId !== sourceId);
  }

  const canPaste = computed(
    () => resolvePasteTargets(useFormBuilderStore().elements).length > 0
  );

  function copy(): boolean {
    const fieldId = clickedFieldId.value;
    if (!canCopy.value || fieldId === null) return false;

    const grant = useFormPermissionsStore().getFieldPermission(fieldId);
    if (!grant) return false;

    clipboard.value = {
      sourceId: fieldId,
      positionIds: [...grant.positionIds],
      functionalActivityIds: [...grant.functionalActivityIds],
    };
    return true;
  }

  // The copy stays, but the destination must be picked again before the next paste.
  function finishPaste() {
    clickedFieldId.value = null;
    pasteItems.value = [];
  }

  return {
    addMode,
    items,
    totalSelected,
    hasItems,
    fillVisible,
    selectAllActive,
    confirmedItems,
    selectedPositionIds,
    selectedFunctionalActivityIds,
    restore,
    leaveStage,
    resolveConfirmedTargets,
    resolveItemTargets,
    clickedFieldId,
    clipboard,
    canCopy,
    canPaste,
    pickField,
    clearPick,
    copy,
    resolvePasteTargets,
    finishPaste,
    isFieldSelected,
    isTableSelected,
    getOutline,
    canUndo: history.canUndo,
    canRedo: history.canRedo,
    undo: history.undo,
    redo: history.redo,
    recordChange,
    startAdd: withHistory(startAdd),
    confirm: withHistory(confirm),
    dismissFill,
    selectField: withHistory(selectField),
    selectTable: withHistory(selectTable),
    selectRow: withHistory(selectRow),
    selectAll: withHistory(selectAll),
    removeItem: withHistory(removeItem),
    clearDraft: withHistory(clearDraft),
    clearItems: withHistory(clearItems),
    reset,
  };
});
